import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiGet, apiPost } from "../../shared/apiClient";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../../shared/ToastContext";

const inputClass =
  "rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20";

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function playChatTone() {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(880, ctx.currentTime);
    oscillator.frequency.setValueAtTime(660, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.32);
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.34);
  } catch {
    /* Browsers can block audio until the user interacts with the page. */
  }
}

export function LiveChatPage() {
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [status, setStatus] = useState("");
  const [summary, setSummary] = useState({ waiting: 0, active: 0, closedToday: 0 });
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const lastVisitorMessageIdRef = useRef({});

  const query = useMemo(() => {
    const parts = [];
    if (selectedCustomerId) parts.push(`customerId=${encodeURIComponent(selectedCustomerId)}`);
    if (status) parts.push(`status=${encodeURIComponent(status)}`);
    return parts.length ? `?${parts.join("&")}` : "";
  }, [selectedCustomerId, status]);

  const summaryQuery = useMemo(
    () => (selectedCustomerId ? `?customerId=${encodeURIComponent(selectedCustomerId)}` : ""),
    [selectedCustomerId]
  );

  const loadList = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      const [summaryRes, rowsRes] = await Promise.all([
        apiGet(`/api/LiveChat/summary${summaryQuery}`),
        apiGet(`/api/LiveChat${query}`)
      ]);
      if (summaryRes.ok) setSummary(summaryRes.data || { waiting: 0, active: 0, closedToday: 0 });
      if (rowsRes.ok) setSessions(Array.isArray(rowsRes.data) ? rowsRes.data : []);
      if (!rowsRes.ok) toast.push(String(rowsRes.error || "Failed to load chat sessions"), "error");
      if (!silent) setLoading(false);
    },
    [query, summaryQuery, toast]
  );

  const loadDetails = useCallback(
    async (id) => {
      if (!id) return;
      const res = await apiGet(`/api/LiveChat/${id}`);
      if (res.ok) {
        const data = res.data;
        const visitorMessages = Array.isArray(data?.messages)
          ? data.messages.filter((m) => m.senderType === "Visitor")
          : [];
        const lastVisitorMessageId = visitorMessages.at(-1)?.id ?? null;
        const previousVisitorMessageId = lastVisitorMessageIdRef.current[id];
        if (
          previousVisitorMessageId &&
          lastVisitorMessageId &&
          lastVisitorMessageId > previousVisitorMessageId
        ) {
          playChatTone();
        }
        if (lastVisitorMessageId) {
          lastVisitorMessageIdRef.current[id] = lastVisitorMessageId;
        }
        setDetails(data);
      }
      else toast.push(String(res.error || "Failed to load chat"), "error");
    },
    [toast]
  );

  useEffect(() => {
    const loadCustomers = async () => {
      if (!isAdmin) return;
      const res = await apiGet("/api/Customers");
      if (res.ok) setCustomers(Array.isArray(res.data) ? res.data : []);
    };
    void loadCustomers();
  }, [isAdmin]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    if (sessions.length > 0 && !activeId) setActiveId(sessions[0].id);
  }, [sessions, activeId]);

  useEffect(() => {
    if (!activeId) {
      setDetails(null);
      return undefined;
    }
    void loadDetails(activeId);
    const timer = window.setInterval(() => {
      void loadDetails(activeId);
      void loadList(true);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [activeId, loadDetails, loadList]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      void loadList(true);
    }, 10000);
    return () => window.clearInterval(timer);
  }, [loadList]);

  const joinChat = async () => {
    if (!activeId) return;
    const res = await apiPost(`/api/LiveChat/${activeId}/join`, {});
    if (!res.ok) {
      toast.push(String(res.error), "error");
      return;
    }
    toast.push("Joined chat", "success");
    await loadDetails(activeId);
    await loadList(true);
  };

  const closeChat = async () => {
    if (!activeId) return;
    const res = await apiPost(`/api/LiveChat/${activeId}/close`, {});
    if (!res.ok) {
      toast.push(String(res.error), "error");
      return;
    }
    toast.push("Chat closed", "success");
    await loadDetails(activeId);
    await loadList(true);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!activeId || !message.trim()) return;
    setSending(true);
    const res = await apiPost(`/api/LiveChat/${activeId}/messages`, { message });
    setSending(false);
    if (!res.ok) {
      toast.push(String(res.error), "error");
      return;
    }
    setMessage("");
    await loadDetails(activeId);
    await loadList(true);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Live Chat</h1>
            <p className="mt-1 text-sm text-slate-500">Visitor chat requests and employee conversations.</p>
          </div>
          <button
            type="button"
            onClick={() => void loadList(true)}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Refresh
          </button>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl bg-amber-50 p-4 text-amber-800">
            <p className="text-xs font-semibold uppercase tracking-wide">Waiting</p>
            <p className="mt-1 text-2xl font-bold">{summary.waiting || 0}</p>
          </div>
          <div className="rounded-xl bg-emerald-50 p-4 text-emerald-800">
            <p className="text-xs font-semibold uppercase tracking-wide">Active</p>
            <p className="mt-1 text-2xl font-bold">{summary.active || 0}</p>
          </div>
          <div className="rounded-xl bg-slate-100 p-4 text-slate-800">
            <p className="text-xs font-semibold uppercase tracking-wide">Closed Today</p>
            <p className="mt-1 text-2xl font-bold">{summary.closedToday || 0}</p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {isAdmin && (
            <select className={inputClass} value={selectedCustomerId} onChange={(e) => setSelectedCustomerId(e.target.value)}>
              <option value="">All customers</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || c.code || `Customer ${c.id}`}
                </option>
              ))}
            </select>
          )}
          <select className={inputClass} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="Waiting">Waiting</option>
            <option value="Active">Active</option>
            <option value="Closed">Closed</option>
          </select>
        </div>
      </div>

      <div className="grid min-h-[560px] gap-4 lg:grid-cols-[360px_minmax(0,1fr)]">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800">
            Chat queue
          </div>
          {loading ? (
            <div className="p-6 text-sm text-slate-500">Loading...</div>
          ) : (
            <div className="max-h-[640px] overflow-y-auto divide-y divide-slate-100">
              {sessions.map((chat) => (
                <button
                  key={chat.id}
                  type="button"
                  onClick={() => setActiveId(chat.id)}
                  className={`block w-full px-4 py-3 text-left hover:bg-slate-50 ${activeId === chat.id ? "bg-emerald-50/70" : "bg-white"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate text-sm font-semibold text-slate-900">{chat.visitorName || "Visitor"}</p>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${chat.status === "Waiting" ? "bg-amber-100 text-amber-800" : chat.status === "Active" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>
                      {chat.status || "-"}
                    </span>
                  </div>
                  <p className="mt-1 truncate text-xs text-slate-500">{chat.customerName || chat.visitorEmail || "-"}</p>
                  <p className="mt-1 text-xs text-slate-400">{formatDateTime(chat.lastMessageAtUtc)}</p>
                </button>
              ))}
              {sessions.length === 0 && <div className="p-6 text-sm text-slate-500">No chat sessions found.</div>}
            </div>
          )}
        </div>

        <div className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {details ? (
            <>
              <div className="border-b border-slate-100 px-5 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">{details.visitorName || "Visitor"}</h2>
                    <p className="mt-1 text-sm text-slate-500">
                      {details.customerName || "-"}{details.visitorEmail ? ` • ${details.visitorEmail}` : ""}
                    </p>
                    {details.pageUrl && <p className="mt-1 truncate text-xs text-slate-400">{details.pageUrl}</p>}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {details.status === "Waiting" && !details.assignedUserId && (
                      <button type="button" onClick={() => void joinChat()} className="rounded-lg bg-brand-800 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700">
                        Join
                      </button>
                    )}
                    {details.status !== "Closed" && (
                      <button type="button" onClick={() => void closeChat()} className="rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50">
                        End Chat
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-slate-50 p-5">
                {(details.messages || []).map((m) => {
                  const mine = m.senderType === "Employee";
                  const system = m.senderType === "System";
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[78%] rounded-2xl px-4 py-2 text-sm shadow-sm ${system ? "bg-slate-200 text-slate-600" : mine ? "bg-brand-800 text-white" : "bg-white text-slate-800"}`}>
                        <p className="text-[11px] font-semibold opacity-70">{m.senderName || m.senderType}</p>
                        <p className="mt-1 whitespace-pre-wrap">{m.message}</p>
                        <p className="mt-1 text-[10px] opacity-60">{formatDateTime(m.createdAtUtc)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <form onSubmit={sendMessage} className="border-t border-slate-100 p-4">
                <div className="flex gap-2">
                  <input
                    className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20"
                    value={message}
                    disabled={details.status === "Closed"}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={details.status === "Closed" ? "Chat is closed" : "Type a reply..."}
                  />
                  <button type="submit" disabled={sending || details.status === "Closed"} className="rounded-lg bg-brand-800 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60">
                    {sending ? "Sending..." : "Send"}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center p-8 text-sm text-slate-500">
              Select a chat to view the conversation.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
